import { type ReactNode, useCallback, useEffect, useState } from 'react'

import { MeetingsMeetingIsManagerDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'

import {
  MeetingsMeetingContext,
  type MeetingsMeetingContextState,
} from 'modules/meetings/lib/context/MeetingsMeetingContext'
import { useOneOnOnesContext } from 'modules/meetings/lib/hooks/useOneOnOnesContext'

import { useAutosave } from './hooks/useAutosave'
import { useMeetingData } from './hooks/useMeetingData'

interface MeetingsMeetingProviderProps {
  children: ReactNode
}

export const MeetingsMeetingProvider = ({ children }: MeetingsMeetingProviderProps) => {
  const { employee: currentEmployee } = useCurrentSession()
  const [notes, setNotesState] = useState<string | null>(null)
  const [meetingId, setMeetingId] = useState<string | null>(null)

  const {
    meeting,
    page,
    notes: initialNotes,
    loading,
    error: meetingDataError,
  } = useMeetingData({ meetingId })
  // TODO CHANGE THIS TO DONT HAVE THE DEPENCENCY ON ONE ON ONES CONTEXT
  const { canSeeAdvancedMeetings } = useOneOnOnesContext()
  const { isSavingNotes, setNotes, getCleanup, error: autosaveError } = useAutosave({ page })

  const otherAttendeeEmployeeId = meeting?.attendees.find(
    (attendee) => attendee.employee?.id !== currentEmployee?.id
  )?.employee?.id

  const { data: isManagerData } = useQuery(MeetingsMeetingIsManagerDocument, {
    variables: {
      employeeId: String(otherAttendeeEmployeeId),
    },
    skip: !otherAttendeeEmployeeId,
  })

  const canUseAI =
    canSeeAdvancedMeetings &&
    (isManagerData?.employees?.employeesConnection?.nodes.some(
      (employee) => employee.id === otherAttendeeEmployeeId
    ) ??
      false)

  // Reset notes state when navigating to a different meeting
  useEffect(() => {
    setNotesState(null)
  }, [meetingId])

  // Sync initial data with local state
  useEffect(() => {
    // Sync when page exists (data is loaded), regardless of notes value
    if (page) {
      setNotesState(initialNotes)
    }
  }, [initialNotes, page, meetingId])

  // Handle notes changes
  const handleNotesChange = useCallback(
    (json: string) => {
      try {
        JSON.parse(json)
        setNotesState(json)
        setNotes(json)
      } catch (e) {
        setNotesState('')
      }
    },
    [setNotes]
  )

  // Cleanup autosave timeout on unmount
  useEffect(() => {
    const cleanup = getCleanup()
    return cleanup
  }, [getCleanup])

  const value: MeetingsMeetingContextState = {
    notes,
    setNotes: handleNotesChange,
    loading,
    isSavingNotes,
    error: (meetingDataError || autosaveError) ?? null,
    page,
    meeting,
    canUseAI,
    meetingId,
    setMeetingId,
  }

  return <MeetingsMeetingContext.Provider value={value}>{children}</MeetingsMeetingContext.Provider>
}
